import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import type {
  FilterChangedEvent,
  GridApi,
  RowClassParams,
  RowDoubleClickedEvent,
} from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useFeatureFlag } from '@/app/services/features';
import { GridStatusBar, WithLocalStorage } from '@/components';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef as sharedColDef, sideBar } from '@/grid/defaults';
import { utcDateFormatter } from '@/grid/value-formatters';
import { ShippingFormExportDialog } from '@/pages/shipping-details/components/shipping-form-export-dialog';
import { PrimaryContext } from '@/services';
import { useShipmentAccountDetails } from '@/services/shipping/hooks';
import type { ShipmentListItem } from '@/services/shipping/types';

import { ClearFiltersButton } from './components/clear-filters-button';
import { ShipFromSiteDropdown } from './components/ship-from-site-dropdown';
import { ShipmentStatusDropdown } from './components/shipment-status-dropdown';
import { useShipmentDataSource } from './hooks/useShipmentDataSource';
import { useShippingContextMenu } from './hooks/useShippingContextMenu';
import { Shipments } from './Shipments';
import { CONTENT_TYPE_FIELDS, useColumns } from './utils/columnDefs';
import { ShipmentStatus } from './utils/enums';
import { shipmentWizardConfig } from './utils/wizardConfig';

const ShippingPageContent = () => {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const toastRef = useRef<Toast>(null);
  const [massEditShipmentIds, setMassEditShipmentIds] = useState<string[]>([]);
  const [showMassEditDialog, setShowMassEditDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<ShipmentListItem[]>([]);

  const handleEditSelected = useCallback((shipmentIds: string[]) => {
    if (!shipmentIds || shipmentIds.length === 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'No Selection',
        detail: 'Please select at least one shipment to edit.',
        life: 3000,
      });
      return;
    }
    setMassEditShipmentIds(shipmentIds);
    setShowMassEditDialog(true);
  }, []);

  const { getContextMenuItems, pendingExport, closeExportDialog } =
    useShippingContextMenu(toastRef, handleEditSelected);
  const { themeMode } = useContext(PrimaryContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const gridRef = useRef<AgGridReact>(null);

  const [quickSearchValue, setQuickSearchValue] = useState('');
  const useOrFilters = useFeatureFlag('shipping_or_content_filters');
  const preserveGridState = useFeatureFlag('Shipping_Preserve_Grid_State');
  const [selectedContentTypeFields, setSelectedContentTypeFields] = useState<
    string[]
  >(useOrFilters ? CONTENT_TYPE_FIELDS : []);

  const contentTypeOptions = useMemo(
    () => [
      { label: t('embryo.embryos'), value: 'hasEmbryos', field: 'hasEmbryos' },
      { label: t('semen.semen'), value: 'hasSemen', field: 'hasSemen' },
      { label: t('calves.calves'), value: 'hasCalves', field: 'hasCalves' },
      {
        label: t('recipients.recips'),
        value: 'hasRecipients',
        field: 'hasRecipients',
      },
      { label: t('donor.donors'), value: 'hasDonors', field: 'hasDonors' },
    ],
    [t],
  );

  const [activeOverlayAccount, setActiveAccount] = useState<
    ShipmentListItem['owner'] | undefined
  >();

  const {
    data: account,
    isLoading: isAccountLoading,
    error: accountError,
  } = useShipmentAccountDetails(activeOverlayAccount?.id);

  const { dataSource } = useShipmentDataSource({
    quickFilterText: quickSearchValue,
    selectedContentTypeFields,
  });
  const columnDefs = useColumns(setActiveAccount, preserveGridState);

  const deriveContentFields = useCallback((api: GridApi) => {
    const filterModel = api.getFilterModel() ?? {};
    return CONTENT_TYPE_FIELDS.filter((field) => {
      const model = filterModel[field] as
        | { filterType: string; values: (boolean | string)[] }
        | undefined;
      return !!model?.values?.some((v) => v === true || v === 'true');
    });
  }, []);

  const setContentFilterModel = useCallback((fields: string[]) => {
    const api = gridRef.current?.api;
    if (!api) return;
    const filterModel = api.getFilterModel() ?? {};
    for (const field of CONTENT_TYPE_FIELDS) {
      if (fields.includes(field)) {
        filterModel[field] = { filterType: 'set', values: [true] };
      } else {
        delete filterModel[field];
      }
    }
    api.setFilterModel(filterModel);
  }, []);

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      setSelectedContentTypeFields(deriveContentFields(e.api));
    },
    [deriveContentFields],
  );

  const clearShippingUrlFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('status');
      next.delete('sIds');
      return next;
    });
  }, [setSearchParams]);

  const shippingSideBar = useMemo(
    () => ({
      ...sideBar,
      toolPanels: sideBar.toolPanels?.map((panel) => {
        if (typeof panel === 'string' || panel.id !== 'customViews') {
          return panel;
        }

        return {
          ...panel,
          toolPanelParams: {
            ...panel.toolPanelParams,
            onClearAllFilters: preserveGridState
              ? clearShippingUrlFilters
              : undefined,
          },
        };
      }),
    }),
    [clearShippingUrlFilters, preserveGridState],
  );

  const gridOptions: AgGridReactProps = useMemo(
    () => ({
      getRowId: ({ data }: { data: ShipmentListItem }) => data.id,
      defaultColDef: {
        ...sharedColDef,
        cellClass: 'text-sm',
        headerClass: 'text-sm',
        filter: true,
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
      columnDefs,
      rowModelType: 'serverSide',
      cacheBlockSize: 20,
      blockLoadDebounceMillis: 500,
      rowSelection: 'multiple',
      rowMultiSelectWithClick: false,
      suppressRowClickSelection: true,
      onSelectionChanged: (e) => {
        const rows = e.api?.getSelectedRows?.() || [];
        setSelectedRows(rows);
      },
      onRowDoubleClicked: (e: RowDoubleClickedEvent) => {
        if (e.data?.id) {
          const params = new URLSearchParams(location.search);
          params.set('id', e.data.id);
          navigate(`/shipping/shipment?${params.toString()}`);
        }
      },
      onFilterChanged,
      rowClassRules: {
        'bg-yellow-100': (p: RowClassParams<ShipmentListItem>) =>
          p?.data?.shipmentStatus === ShipmentStatus.IN_PROGRESS,
        'text-yellow-900': (p: RowClassParams<ShipmentListItem>) =>
          p?.data?.shipmentStatus === ShipmentStatus.IN_PROGRESS,
      },
      statusBar: {
        statusPanels: [
          {
            statusPanel: GridStatusBar,
            statusPanelParams: { entityLabel: 'shipments' },
            align: 'left' as const,
          },
        ],
      },
    }),
    [columnDefs, location.search, navigate, onFilterChanged],
  );

  const agingAmount = (v?: number | null) =>
    Number(v ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const title = useMemo(() => {
    const shipmentNumbers = massEditShipmentIds
      .map((id) => {
        const node = gridRef.current?.api?.getRowNode(id);
        return (node?.data as ShipmentListItem)?.shipmentNumber;
      })
      .filter(Boolean);

    if (shipmentNumbers.length === 0) {
      return `${t('shipping.editShipments')} (${massEditShipmentIds.length})`;
    }

    const label =
      massEditShipmentIds.length === 1
        ? t('shipping.editShipment')
        : t('shipping.editShipments');
    const numbers =
      massEditShipmentIds.length === 1
        ? shipmentNumbers[0]
        : shipmentNumbers.join(', ');

    return `${label}: ${numbers}`;
  }, [massEditShipmentIds, t]);

  return (
    <>
      <ShippingFormExportDialog
        open={pendingExport !== null}
        shipmentIds={pendingExport?.shipmentIds ?? []}
        format={pendingExport?.format ?? 'pdf'}
        onClose={closeExportDialog}
      />
      <Toast
        ref={toastRef}
        position="bottom-right"
        className="fixed right-0 bottom-0 z-50"
      />
      <Dialog
        visible={!!activeOverlayAccount}
        header={
          activeOverlayAccount
            ? `${activeOverlayAccount.code}: ${activeOverlayAccount.name}`
            : undefined
        }
        className="max-w-[50rem]"
        onHide={() => {
          setActiveAccount(undefined);
        }}
        dismissableMask
      >
        {account && account.id === activeOverlayAccount?.id ? (
          <div className="flex flex-col gap-2">
            <p className="m-0">
              <b>{t('roles.csrLabel')}: </b>

              {account.customerServiceRepresentative?.fullName ??
                t('account.accountHasNoCsr')}
            </p>
            <p className="m-0">
              <b>{t('account.ranchAddress')}: </b>

              {account.ranchAddress?.composite ??
                t('account.accountHasNoRanchAddress')}
            </p>
            <p className="m-0">
              <b>{t('account.billingAddress')}: </b>

              {account.billingAddress?.composite ??
                t('account.accountHasNoBillingAddress')}
            </p>
            <div className="grid grid-cols-[8rem_8rem] items-baseline gap-x-4 gap-y-1">
              <span className="font-semibold">{t('account.aging0to30')}:</span>
              <span className="tabular-nums">
                {agingAmount(account?.aging30)}
              </span>

              <span className="font-semibold">{t('account.aging31to60')}:</span>
              <span className="tabular-nums">
                {agingAmount(account?.aging60)}
              </span>

              <span className="font-semibold">{t('account.aging61to90')}:</span>
              <span className="tabular-nums">
                {agingAmount(account?.aging90)}
              </span>

              <span className="font-semibold">
                {t('account.aging91to120')}:
              </span>
              <span className="tabular-nums">
                {agingAmount(account?.aging120)}
              </span>

              <span className="font-semibold">
                {t('account.aging121to180')}:
              </span>
              <span className="tabular-nums">
                {agingAmount(account?.aging180)}
              </span>

              <span className="font-semibold">
                {t('account.aging181to365')}:
              </span>
              <span className="tabular-nums">
                {agingAmount(account?.aging365)}
              </span>

              <span className="font-semibold">
                {t('account.aging365plus')}:
              </span>
              <span className="tabular-nums">
                {agingAmount(account?.aging365Plus)}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="m-0">{t('common.notes')}</h3>

              {account.annotations.length ? (
                account.annotations.map((note) => (
                  <p className="m-0">
                    <b>{note.owner?.name}</b>
                    {note.createdOn
                      ? ` (${utcDateFormatter({ value: note.createdOn })})`
                      : null}
                    <br />
                    {note.noteText}
                  </p>
                ))
              ) : (
                <p>{t('account.accountHasNoNotes')}</p>
              )}
            </div>
          </div>
        ) : isAccountLoading ? (
          <p>{t('common.loading')}</p>
        ) : accountError ? (
          <p>{t('account.errorLoadingAccountDetails')}</p>
        ) : null}
      </Dialog>

      <div className="common-page flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-4">
            <ShipFromSiteDropdown grid={gridRef} />
            <MultiSelect
              value={
                Array.isArray(selectedContentTypeFields)
                  ? selectedContentTypeFields
                  : []
              }
              onChange={(e) => {
                setContentFilterModel(Array.isArray(e.value) ? e.value : []);
              }}
              placeholder={t('shipping.filterByContent')}
              options={contentTypeOptions}
              optionLabel="label"
              optionValue="field"
              display="chip"
            />
            <ShipmentStatusDropdown grid={gridRef} />

            <ClearFiltersButton
              selectedContentTypeFields={selectedContentTypeFields}
              gridRef={gridRef}
              onClick={() => {
                gridRef.current?.api?.setFilterModel(null);
                gridRef.current?.api?.onFilterChanged();
                if (preserveGridState) {
                  clearShippingUrlFilters();
                }
              }}
            />
          </div>
          <div className="flex gap-2">
            {preserveGridState && (
              <Button
                icon="pi pi-refresh"
                severity="secondary"
                onClick={() => {
                  gridRef.current?.api?.refreshServerSide({ purge: true });
                }}
              />
            )}
            <Button
              label={t('common.editSelected')}
              icon="pi pi-pencil"
              badge={
                selectedRows.length > 0
                  ? selectedRows.length.toString()
                  : undefined
              }
              badgeClassName="p-badge-danger"
              size="small"
              disabled={
                selectedRows.length === 0 ||
                selectedRows.some(
                  (r) => r.shipmentStatus === ShipmentStatus.SHIPPED,
                )
              }
              tooltip={
                selectedRows.some(
                  (r) => r.shipmentStatus === ShipmentStatus.SHIPPED,
                )
                  ? t('shipping.cannotEditShippedStatus')
                  : undefined
              }
              onClick={() => {
                const ids = selectedRows.map((r) => r.id);
                handleEditSelected(ids);
              }}
            />
            <InputText
              placeholder={t('common.search')}
              value={quickSearchValue ?? ''}
              onChange={(e) => setQuickSearchValue(e.target.value ?? '')}
            />
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <WithLocalStorage storageKey="shipping-grid2">
            <AgGridReact
              gridOptions={defaultGridOptions}
              getContextMenuItems={getContextMenuItems}
              ref={gridRef}
              {...gridOptions}
              serverSideDatasource={dataSource}
              reactiveCustomComponents
              sideBar={shippingSideBar}
              className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full w-full`}
            />
          </WithLocalStorage>
        </div>
      </div>

      {showMassEditDialog && massEditShipmentIds.length > 0 && (
        <Dialog
          header={title}
          visible={showMassEditDialog}
          className="w-[90vw] max-w-[1200px]"
          draggable
          onHide={() => {
            setShowMassEditDialog(false);
            setMassEditShipmentIds([]);
          }}
          dismissableMask
        >
          <Shipments
            shipmentIds={massEditShipmentIds}
            rows={selectedRows.filter((r) =>
              massEditShipmentIds.includes(r.id),
            )}
            config={shipmentWizardConfig}
            onSuccess={() => {
              setShowMassEditDialog(false);
              setMassEditShipmentIds([]);
              gridRef.current?.api?.refreshServerSide?.({ purge: false });
            }}
            onCancel={() => {
              setShowMassEditDialog(false);
              setMassEditShipmentIds([]);
            }}
            themeMode={themeMode}
          />
        </Dialog>
      )}
    </>
  );
};

export const ShippingPage = () => (
  <ConfirmProvider>
    <ShippingPageContent />
  </ConfirmProvider>
);
